// manage-assessment-response-form.component.ts

import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subject, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpErrorResponse } from '@angular/common/http';

import { ApiService, Region, Country } from "../../../../../core/services/api/api.service";
import { ManageAssessmentApiService, ClassificationsStatus, DeviceClassifications, ProductDesignHistory, ChangeTypes, ProductFamilies } from "../../../../../core/services/api/manage-assessment-api.service";

import { PrimeNGSharedModule } from "../../../../../shared/prime/prime-ng.module";
import { MessageService } from "primeng/api";
import { CalendarModule } from 'primeng/calendar';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';

import { ManageResponseNextFormComponent } from '../manage-response-next-form/manage-response-next-form.component';


@Component({
  selector: "app-manage-assessment-response-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNGSharedModule,
    CalendarModule,
    MultiSelectModule,
    DropdownModule,
    EditorModule,
    ManageResponseNextFormComponent,
  ],
  templateUrl: "./manage-assessment-response-form.component.html",
  styleUrl: "./manage-assessment-response-form.component.scss",
  providers: [MessageService, DatePipe],
})
export class ManageAssessmentResponseFormComponent
  implements OnInit, OnDestroy
{
  @Input() visible: boolean = false;
  @Input() reviseResponse: boolean = false;
  @Input() isReviseCheck: boolean = false;
  @Input() mappingId: any = null; // This should contain response_mapping_id
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() rcaPatched = new EventEmitter<void>();
  @Output() responseDraft = new EventEmitter<void>();

  @ViewChild(ManageResponseNextFormComponent)
  nextFormComponent!: ManageResponseNextFormComponent;

  rcaForm!: FormGroup;
  formSubmitted: boolean = false;

  countries: Country[] = [];
  regions: Region[] = [];
  allCountries: Country[] = [];
  usMedDeviceOptions: DeviceClassifications[] = [];
  usClassifications: ClassificationsStatus[] = [];
  euMedDeviceOptions: DeviceClassifications[] = [];
  typeOfChanges: ChangeTypes[] = [];
  euClassifications: ClassificationsStatus[] = [];
  productFamilies: ProductFamilies[] = [];
  productDesignHistoryOptions: ProductDesignHistory[] = [];

  showFirstPage: boolean = true;
  showNextPage: boolean = false;
  // No need for formDataForNextPage to hold the *entire* second form's data.
  // It will only hold the initial values from the first form that the second form needs.
  formDataForNextPage: any = {};
  
  // Track whether "Other(s)" is selected in Product Design History Documentation
  showOtherDesignDocsField: boolean = false;

  /** Full-screen p-editor for change description (same toolbar/modules as inline editor) */
  changeDescriptionMaxPreview = false;

  /** Full-screen read-only HTML view when the RCA form is disabled */
  changeDescriptionReadonlyMaxVisible = false;

  readonly changeDescriptionMaxEditorStyle = { height: "calc(100vh - 140px)" };

  /** Shared by editor fullscreen and read-only fullscreen dialogs */
  readonly changeDescriptionMaxDialogContentStyle: Record<string, string | number> = {
    overflow: "hidden",
    display: "flex",
    "flex-direction": "column",
    flex: "1 1 auto",
    "min-height": 0,
  };

  // Properties to store RCA uploaded files for display
  rcaUploadedFiles: any[] = []; // new multi-file array
  rcaUploadedFile: any = null; // legacy single-file (first attachment) retained for any existing usages

  private readonly unsubscribe$ = new Subject<void>();
  responseFormData: any; // This will hold the combined RCA and Response data

  // Quill editor configuration
  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  get changeDescriptionInvalid(): boolean {
    return this.formSubmitted && !!this.rcaForm?.get("changeDescription")?.invalid;
  }

  constructor(
  private readonly fb: FormBuilder,
  private readonly manageAssessmentApiService: ManageAssessmentApiService,
  private readonly apiService: ApiService,
  private readonly messageService: MessageService,
  private readonly datePipe: DatePipe,
  private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadDropdownData(); // Load all dropdowns first
    this.setupDependentDropdowns();
    // Fetch the combined RCA and Response data
  if (this.mappingId?.response_mapping_id) {
      this.fetchResponseFormAndRCAData(this.mappingId.response_mapping_id);
    }
  }

  // Combined fetch for RCA and Response data
  fetchResponseFormAndRCAData(responseMappingId: number) {
    this.manageAssessmentApiService
      .getResponseRcaForm(responseMappingId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (responseRcaForm: any) => {
          // Now fetch the RCA details
          this.manageAssessmentApiService.getManageAssessmentByRca(this.mappingId.rca_id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: (rcaDetails: any) => {
                const responseAttachments = Array.isArray(responseRcaForm?.attachments)
                  ? responseRcaForm.attachments
                  : [];
                const rcaAttachments = Array.isArray(rcaDetails?.attachments)
                  ? rcaDetails.attachments
                  : [];

                // Combine all data into a single object while preserving distinct attachment sources.
                this.responseFormData = {
                  ...this.mappingId, // Contains response_mapping_id, rca_id etc.
                  ...responseRcaForm, // Contains response-specific fields like device_class, impact_conclusion etc.
                  ...rcaDetails, // Contains RCA-specific fields like regions, countries, change_types etc.
                  responseAttachments,
                  rcaAttachments,
                };
                // Ensure all dropdown data is loaded before patching
                this.loadAllDropdownsAndPatchForm(this.responseFormData);
              },
              error: (err: HttpErrorResponse) => {
                console.error("Failed to fetch RCA details:", err);
                this.messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: "Failed to fetch RCA details. Please try again.",
                  life: 5000,
                });
              }
            });
        },
        error: (err: HttpErrorResponse) => {
          console.error("Failed to fetch response rca form data:", err);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to fetch response form data. Please try again.",
            life: 5000,
          });
        }
      });
  }

  loadAllDropdownsAndPatchForm(dataToPatch: any) {
    Promise.all([
      firstValueFrom(this.apiService.getRegions()),
      firstValueFrom(this.apiService.getCountries()),
      firstValueFrom(this.manageAssessmentApiService.getClassifications()),
      firstValueFrom(this.manageAssessmentApiService.getUsDeviceClassifications()),
      firstValueFrom(this.manageAssessmentApiService.getEuDeviceClassifications()),
      firstValueFrom(this.manageAssessmentApiService.getProductDesignHistoryDoc()),
      firstValueFrom(this.manageAssessmentApiService.getChangeTypes()),
      firstValueFrom(this.manageAssessmentApiService.getProductFamilies()),
    ])
      .then(
        ([
          regionsResponse,
          countriesResponse,
          classificationsResponse,
          usDeviceClassificationsResponse,
          euDeviceClassificationsResponse,
          productDesignHistoryResponse,
          changeTypesResponse,
          productFamiliesResponse,
        ]) => {
          this.regions = regionsResponse?.regions || [];
          this.allCountries = countriesResponse?.countries || [];
          this.countries = [...this.allCountries]; // Initialize with all countries
          this.usClassifications =
            classificationsResponse?.classifications_status || [];
          this.euClassifications =
            classificationsResponse?.classifications_status || [];
          this.usMedDeviceOptions =
            usDeviceClassificationsResponse?.us_device_classifications || [];
          this.euMedDeviceOptions =
            euDeviceClassificationsResponse?.eu_device_classifications || [];
          this.productDesignHistoryOptions =
            productDesignHistoryResponse?.product_design_history_docs || [];
          this.typeOfChanges = changeTypesResponse?.change_types || [];
          this.productFamilies =
            productFamiliesResponse?.product_families || [];

          // Now that all dropdown data is loaded, patch the form
          this.patchFormWithRcaData(dataToPatch);
        }
      )
      .catch((error) => {
        console.error("Failed to load all dropdown data for patching:", error);
        this.messageService.add({
          severity: "error",
          summary: "Data Load Error",
          detail: "Failed to load necessary data for form. Please try again.",
          life: 5000,
        });
      });
  }

  setupDependentDropdowns(): void {
    const responsibleRegionControl = this.rcaForm.get("region");
    const responsibleCountryControl = this.rcaForm.get("country");

    responsibleRegionControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedRegions: Region[] | null) => {
        responsibleCountryControl?.reset([]); // Reset countries when regions change
        this.countries = []; // Clear current countries
        if (selectedRegions && selectedRegions.length > 0) {
          const selectedRegionIds = new Set<number>(selectedRegions.map((region) => region.id));
          this.countries = this.allCountries.filter((country) =>
            selectedRegionIds.has(country.region.id)
          );
          responsibleCountryControl?.enable();
        } else {
          responsibleCountryControl?.disable();
          this.countries = [];
        }
      });
  }

  loadDropdownData(): void {
    // This method is now primarily for initial loading, but `loadAllDropdownsAndPatchForm`
    // will ensure they are fully loaded before patching.
    // You can keep this for initial UI rendering if needed, but the Promise.all
    // in `loadAllDropdownsAndPatchForm` is the critical part for patching.
    this.apiService.getRegions().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { regions: Region[] }) => this.regions = response.regions,
      error: (err: HttpErrorResponse) => console.error("Failed to load regions", err),
    });

    this.apiService.getCountries().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { countries: Country[] }) => {
        this.allCountries = response.countries;
        this.countries = [...this.allCountries];
      },
      error: (err: HttpErrorResponse) => console.error("Failed to load countries", err),
    });

    this.manageAssessmentApiService.getClassifications().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { classifications_status: ClassificationsStatus[] }) =>
        (this.usClassifications = this.euClassifications = response.classifications_status),
      error: (err: HttpErrorResponse) => console.error("Failed to load US/EU classifications", err),
    });

    this.manageAssessmentApiService.getUsDeviceClassifications().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { us_device_classifications: DeviceClassifications[]; }) => (this.usMedDeviceOptions = response.us_device_classifications),
      error: (err: HttpErrorResponse) => console.error("Failed to load US device classifications", err),
    });

    this.manageAssessmentApiService.getEuDeviceClassifications().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { eu_device_classifications: DeviceClassifications[]; }) => (this.euMedDeviceOptions = response.eu_device_classifications),
      error: (err: HttpErrorResponse) => console.error("Failed to load EU device classifications", err),
    });

    this.manageAssessmentApiService.getProductDesignHistoryDoc().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { product_design_history_docs: ProductDesignHistory[]; }) =>
        (this.productDesignHistoryOptions = response.product_design_history_docs),
      error: (err: HttpErrorResponse) => console.error("Failed to load product design history documents", err),
    });

    this.manageAssessmentApiService.getChangeTypes().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { change_types: ChangeTypes[] }) => (this.typeOfChanges = response.change_types),
      error: (err: HttpErrorResponse) => console.error("Failed to load type of changes", err),
    });

    this.manageAssessmentApiService.getProductFamilies().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: { product_families: ProductFamilies[] }) => (this.productFamilies = response.product_families),
      error: (err: HttpErrorResponse) => console.error("Failed to load product families", err),
    });
  }

  initializeForm(): void {
    this.rcaForm = this.fb.group({
      title: ["", Validators.required],
      region: [[], Validators.required],
      usMedDeviceClass: [null, Validators.required],
      productDesignHistoryDocumentation: [[], Validators.required],
      otherDesignDocs: [""], // New field for "Other Design Docs"
      country: [[], Validators.required],
      usClassification: [null, Validators.required],
      changeDescription: ["", Validators.required],
      ecrnEcnNumber: [""],
      euMedDeviceClass: [null, Validators.required],
      plannedTimeframeForImplementation: ["", Validators.required],
      typeOfChange: [[], Validators.required],
      euClassification: [null, Validators.required],
      additionalAttachmentsDescription: [""],
      productFamily: [[], Validators.required],
      issuedDate: [null, Validators.required],
      catalogueNumber: ["", Validators.required],
      dueDate: [null, Validators.required],
      attachmentUrls: [[]],
    });
    
    // Set up tracking for "Other(s)" selection
    this.setupOtherDesignDocsTracking();
  }

  setupOtherDesignDocsTracking(): void {
    const productDesignHistoryControl = this.rcaForm.get('productDesignHistoryDocumentation');
    const otherDesignDocsControl = this.rcaForm.get('otherDesignDocs');

    productDesignHistoryControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedOptions: ProductDesignHistory[] | null) => {
        // Check if "Other(s)" is selected
        const hasOtherSelected = selectedOptions?.some(option => 
          option.name?.toLowerCase().includes('other')
        ) || false;
        
        this.showOtherDesignDocsField = hasOtherSelected;
        
        // Add/remove validation based on whether "Other(s)" is selected
        if (hasOtherSelected) {
          otherDesignDocsControl?.setValidators([Validators.required]);
        } else {
          otherDesignDocsControl?.clearValidators();
          // Only clear the field value if there's no existing value from API
          if (!otherDesignDocsControl?.value) {
            otherDesignDocsControl?.setValue('');
          }
        }
        otherDesignDocsControl?.updateValueAndValidity();
      });

    // Trigger initial check in case form already has values
    const currentSelection = productDesignHistoryControl?.value;
    if (currentSelection) {
      const hasOtherSelected = currentSelection.some((option: ProductDesignHistory) => 
        option.name?.toLowerCase().includes('other')
      );
      this.showOtherDesignDocsField = hasOtherSelected;
      
      if (hasOtherSelected) {
        otherDesignDocsControl?.setValidators([Validators.required]);
      } else {
        otherDesignDocsControl?.clearValidators();
      }
      otherDesignDocsControl?.updateValueAndValidity();
    }
  }

  patchFormWithRcaData(rca: any) {
    console.log("Patching first form with RCA data:", rca);
    if (!rca) {
      console.warn("No RCA data provided for patching.");
      return;
    }

    // Helper to find object by name in dropdown options
    const findByName = (arr: any[], name: string) =>
      arr.find((item) => item.name === name);

    this.rcaForm.patchValue({
      title: rca.title || "",
      usMedDeviceClass: findByName(this.usMedDeviceOptions, rca.us_med_class_id),
      usClassification: findByName(this.usClassifications, rca.us_class_status),
      euMedDeviceClass: findByName(this.euMedDeviceOptions, rca.eu_med_class_id),
      euClassification: findByName(this.euClassifications, rca.eu_class_status),
      ecrnEcnNumber: rca.ecn_ern,
      catalogueNumber: rca.catalogue_no,
      changeDescription: rca.description,
      plannedTimeframeForImplementation: rca.planned_timeline,
      issuedDate: rca.issue_date ? new Date(rca.issue_date) : null,
      dueDate: rca.due_date ? new Date(rca.due_date) : null,
      additionalAttachmentsDescription: rca.attachment_description || "",
    });

    this.patchRegionsAndCountries(rca, findByName);
    this.patchRcaMultiSelects(rca, findByName);

    // Handle RCA uploaded files display from the RCA details payload only.
    this.updateRcaAttachments(rca.rcaAttachments ?? rca.attachments ?? []);

    this.rcaForm.disable(); // Disable the first form after patching
    this.rcaPatched.emit();
    this.cdr.detectChanges();

    // Prepare data for the next page (only initial values from first form)
    this.formDataForNextPage = {
      deviceClassificationName: rca.device_class ? this.getDeviceClassificationName(rca.device_class) : '',
  impactConclusionName: this.getImpactConclusionName(rca.impact_conclusion),
      // Pass the full response data object to the child component
      // The child component will then use this to patch its own form
      fullResponseData: rca // Pass the entire combined object
    };
  }

  // Helper to get device classification name from ID
  getDeviceClassificationName(id: number): string {
    const deviceClass = this.nextFormComponent?.deviceClassificationsOptions.find(opt => opt.id === id);
    return deviceClass ? deviceClass.name : '';
  }

  private patchRegionsAndCountries(rca: any, findByName: (arr: any[], name: string) => any): void {
    const regionControl = this.rcaForm.get("region");
    const countryControl = this.rcaForm.get("country");

    if (!regionControl) {
      return;
    }

    if (rca.regions && this.regions.length > 0) {
      const selectedRegions = rca.regions
        .map((regionName: string) => findByName(this.regions, regionName))
        .filter(Boolean);
      regionControl.patchValue(selectedRegions);
      this.cdr.detectChanges();

      if (selectedRegions.length > 0) {
        const selectedRegionIds = new Set(selectedRegions.map((region: any) => region.id));
        this.countries = this.allCountries.filter((country) =>
          selectedRegionIds.has(country.region.id)
        );
        countryControl?.enable();
      } else {
        this.countries = [];
        countryControl?.disable();
      }
    } else {
      regionControl.patchValue([]);
      this.countries = [];
      countryControl?.disable();
    }

    setTimeout(() => {
      if (!countryControl) {
        return;
      }

      if (rca.countries && this.allCountries.length > 0) {
        const selectedCountries = rca.countries
          .map((countryName: string) => findByName(this.allCountries, countryName))
          .filter(Boolean);
        countryControl.patchValue(selectedCountries);
      } else {
        countryControl.patchValue([]);
      }
      this.cdr.detectChanges();
    }, 100);
  }

  private patchRcaMultiSelects(rca: any, findByName: (arr: any[], name: string) => any): void {
    if (rca.change_types && this.typeOfChanges.length > 0) {
      const selectedChangeTypes = rca.change_types
        .map((typeName: string) => findByName(this.typeOfChanges, typeName))
        .filter(Boolean);
      this.rcaForm.get("typeOfChange")?.patchValue(selectedChangeTypes);
    }

    if (rca.product_families && this.productFamilies.length > 0) {
      const selectedProductFamilies = rca.product_families
        .map((familyName: string) => findByName(this.productFamilies, familyName))
        .filter(Boolean);
      this.rcaForm.get("productFamily")?.patchValue(selectedProductFamilies);
    }

    if (rca.design_history_docs && this.productDesignHistoryOptions.length > 0) {
      const selectedDesignHistoryDocs = rca.design_history_docs
        .map((docName: string) => findByName(this.productDesignHistoryOptions, docName))
        .filter(Boolean);
      this.rcaForm.get("productDesignHistoryDocumentation")?.patchValue(selectedDesignHistoryDocs);

      const hasOtherSelected = selectedDesignHistoryDocs.some((doc: ProductDesignHistory) =>
        doc.name?.toLowerCase().includes('other')
      );
      this.showOtherDesignDocsField = hasOtherSelected;
    }

    if (rca.other_design_docs) {
      this.rcaForm.get("otherDesignDocs")?.patchValue(rca.other_design_docs);
    }
  }

  private updateRcaAttachments(attachments: any[]): void {
    this.rcaUploadedFiles = [];
    if (!attachments?.length) {
      this.rcaUploadedFile = null;
      return;
    }
    attachments.forEach(att => {
      if (!att) return;
      let name = 'RCA Attachment';
      let url = '';
      if (att.file_name && att.file_name !== 'Uploaded File') {
        name = att.file_name;
        url = att.url || att.file_url || (att.id ? `/api/attachments/download/${att.id}/` : '');
      } else if (att.bucket_name && att.file_path) {
        name = att.file_path.split('/').pop() || 'Unknown File';
        url = att.id ? `/api/attachments/download/${att.id}/` : '';
      } else if (att.id) {
        name = att.file_name || (att.file_path ? att.file_path.split('/').pop() : 'RCA Attachment');
        url = `/api/attachments/download/${att.id}/`;
      }
      this.rcaUploadedFiles.push({
        name,
        url,
        id: att.id,
        path: att.file_path,
        status: att.status || 'unknown',
        uploadedAt: att.uploaded_at
      });
    });
    // Maintain legacy single reference as first item
    this.rcaUploadedFile = this.rcaUploadedFiles[0] || null;
  }

  private getImpactConclusionName(impactConclusion: boolean | null | undefined): string {
    if (impactConclusion === true) {
      return 'Yes';
    }

    if (impactConclusion === false) {
      return 'No';
    }

    return '';
  }


  goToNextPage(): void {
    this.formSubmitted = true;
    // No need for setTimeout here for country patching, as it's already handled during initial patch
    // and the form is disabled.
    this.showFirstPage = false;
    this.showNextPage = true;
    this.cdr.detectChanges();
  }

  goBackToFirstPage(): void {
    // No need for country patching here, as the form is disabled and values are set.
    this.showNextPage = false;
    this.showFirstPage = true;
    this.rcaForm.disable();
    this.cdr.detectChanges();
  }

  saveNextForm(): void {
    if (this.nextFormComponent) {
      this.nextFormComponent.saveForm();
    }
  }

  submitNextForm(): void {
    if (this.nextFormComponent) {
      this.nextFormComponent.submitForm();
    }
  }

  handleNextFormSubmission(data: any): void {
    this.close(data);
  }

  handleNextFormSave(data: any): void {
    // The data emitted from the child is already the payload for the second form.
    // You might want to combine it with some first form data if needed for a full draft save.
    const combinedFormData = { ...this.rcaForm.getRawValue(), ...data };
    this.responseDraft.emit(combinedFormData);
  }

  close(action?: any): void {
    this.changeDescriptionMaxPreview = false;
    this.changeDescriptionReadonlyMaxVisible = false;
    this.visible = false;
    this.visibleChange.emit(this.visible);
    action ? this.closeDialog.emit(action) : this.closeDialog.emit();
    this.showFirstPage = true;
    this.showNextPage = false;
  }

  openChangeDescriptionMaxPreview(): void {
    this.changeDescriptionMaxPreview = true;
  }

  closeChangeDescriptionMaxPreview(): void {
    this.changeDescriptionMaxPreview = false;
  }

  openChangeDescriptionReadonlyMax(): void {
    this.changeDescriptionReadonlyMaxVisible = true;
  }

  closeChangeDescriptionReadonlyMax(): void {
    this.changeDescriptionReadonlyMaxVisible = false;
  }

  // Method to handle RCA file download for LRA users
  async downloadRcaFile(url: string, fileName: string): Promise<void> {
    // Check if file has a status and handle accordingly
    const fileStatus = this.rcaUploadedFile?.status;
    
    if (fileStatus === "failed") {
      this.messageService.add({
        severity: "error",
        summary: "Download Failed",
        detail: "This file upload failed and cannot be downloaded.",
        life: 5000,
      });
      return;
    }
    
    if (fileStatus === "pending") {
      this.messageService.add({
        severity: "warn",
        summary: "File Pending",
        detail: "This file is still pending verification. Please try again later.",
        life: 5000,
      });
      return;
    }
    
    if (fileStatus === "scanning") {
      this.messageService.add({
        severity: "warn",
        summary: "File scanning",
        detail: "This file is still being processed. Please try again later.",
        life: 5000,
      });
      return;
    }

    const isApiDownloadUrl = url.includes("/api/attachments/download/");
    if (isApiDownloadUrl) {
      try {
        const res: any = await firstValueFrom(
          this.apiService.getSignedAttachmentDownloadFromUrl(url)
        );
        if (res?.download_url) {
          await this.triggerFileDownload(res.download_url, fileName);
          this.messageService.add({
            severity: "success",
            summary: "Download Started",
            detail: "Your download should begin shortly.",
            life: 3000,
          });
        } else {
          this.messageService.add({
            severity: "error",
            summary: "Download Error",
            detail: "Failed to get a valid download link from the server.",
            life: 5000,
          });
        }
      } catch (err: any) {
        console.error("Failed to get signed attachment URL:", err);
        this.messageService.add({
          severity: "error",
          summary: "Download Failed",
          detail:
            err.error?.message || "Unable to download file. Please try again.",
          life: 5000,
        });
      }
    } else {
      // Direct download for Firebase URLs
      try {
        await this.triggerFileDownload(url, fileName);
        this.messageService.add({
          severity: "success",
          summary: "Download Started",
          detail: "Your download should begin shortly.",
          life: 3000,
        });
      } catch (err) {
        console.error("Failed to download file directly:", err);
        this.messageService.add({
          severity: "error",
          summary: "Download Failed",
          detail: "Unable to download file. Please try again.",
          life: 5000,
        });
      }
    }
  }
  private async triggerFileDownload(url: string, fileName = 'download'): Promise<void> {
    try {
      // Fetch the file as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      
      // Create a blob URL and trigger download
      const blobUrl = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        link.remove();
        globalThis.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to direct link method if fetch fails
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
      }, 100);
    }
  }  // Helper method to get RCA file status display information
  getRcaFileStatusInfo(file: any = this.rcaUploadedFile): { icon: string; class: string; text: string; canDownload: boolean } {
    if (!file?.status) {
      return { icon: 'pi-file', class: 'text-gray-600', text: '', canDownload: true };
    }
    switch (file.status) {
      case 'success':
      case 'passed': // Add support for "passed" status from API
        return { 
          icon: 'pi-check-circle', 
          class: 'text-green-600', 
          text: 'Upload successful', 
          canDownload: true 
        };
      case 'pending':
         return { 
          icon: 'pi-clock', 
          class: 'text-yellow-600', 
          text: 'Pending...', 
          canDownload: false 
        };
      case 'scanning':
        return { 
          icon: 'pi-clock', 
          class: 'text-yellow-600', 
          text: 'Scanning...', 
          canDownload: false 
        };
      case 'failed':
        return { 
          icon: 'pi-times-circle', 
          class: 'text-red-600', 
          text: 'Upload failed', 
          canDownload: false 
        };
      default:
        return { 
          icon: 'pi-question-circle', 
          class: 'text-gray-600', 
          text: 'Unknown status', 
          canDownload: false 
        };
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Check if text length exceeds a certain threshold to show tooltip
   * @param text The text to check
   * @param maxLength Maximum length before showing tooltip (default 30)
   */
  shouldShowTooltip(text: string | undefined, maxLength: number = 20): boolean {
    return text ? text.length > maxLength : false;
  }
}